import type { ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";

interface Props {
  children: ReactNode;
  adminOnly?: boolean;
  requireSubscription?: boolean;
  requireAuth?: boolean;
  requireJobPostingAccess?: boolean;
}

export default function ProtectedRoute({
  children,
  adminOnly = false,
  requireSubscription = true,
  requireAuth = true,
  requireJobPostingAccess = false,
}: Props) {
  const { user, isTokenValid } = useAuth();
  const location = useLocation();

  // Development bypass - check environment variable
  const bypassAuth = import.meta.env.VITE_REACT_APP_BYPASS_AUTH === "true";

  if (bypassAuth) {
    console.warn("Development mode: Bypassing authentication");
    return <>{children}</>;
  }

  // Check if token is valid if user exists
  if (user && !isTokenValid()) {
    console.warn("Invalid token detected, redirecting to login");
    // Store the intended destination for after login
    const redirectPath = location.pathname + location.search;
    sessionStorage.setItem("redirectAfterLogin", redirectPath);
    return <Navigate to="/login" replace />;
  }

  // Not logged in
  if (!user && requireAuth) {
    // Special handling for payment-related routes
    const isPaymentRoute =
      location.pathname.includes("/payment") ||
      location.pathname.includes("/subscribe") ||
      (location.pathname.includes("/packages") &&
        location.search.includes("payment=true")) ||
      location.pathname === "/checkout";

    if (isPaymentRoute) {
      console.log(
        "Payment attempt without authentication, redirecting to login",
      );
      // Store the intended payment destination
      sessionStorage.setItem(
        "redirectAfterLogin",
        location.pathname + location.search,
      );
      return <Navigate to="/login" replace />;
    }

    // Special case: if trying to access /find-job without login, redirect to packages
    if (location.pathname === "/find-job") {
      return <Navigate to="/packages" replace />;
    }

    // For all other protected routes, redirect to login
    return <Navigate to="/login" replace />;
  }

  // Admin only route but user is not admin
  if (adminOnly && user?.role !== "admin" && user?.role !== "super_admin") {
    return <Navigate to="/dashboard" replace />;
  }

  // Check job posting access for routes that require it
  if (requireJobPostingAccess && user) {
    const isAdmin = user?.role === "admin" || user?.role === "super_admin";

    // Check if user has job posting capabilities
    const canAccessJobPosting =
      isAdmin ||
      user?.subscriptionState?.capabilities?.canPostJobs ||
      user?.subscriptionState?.subscriptions?.isWhitelisted ||
      user?.subscriptionState?.whitelist?.isActive ||
      user?.isWhitelisted;

    if (!canAccessJobPosting) {
      console.log(
        "User requires job posting access, redirecting to subscriptions",
      );
      console.log("User job posting status:", {
        isAdmin,
        canPostJobs: user.subscriptionState?.capabilities?.canPostJobs,
        isWhitelisted: user.subscriptionState?.subscriptions?.isWhitelisted,
        whitelistActive: user.subscriptionState?.whitelist?.isActive,
        userWhitelisted: user.isWhitelisted,
      });
      return <Navigate to="/dashboard/subscriptions" replace />;
    }
  }

  // FIXED: Allow profile and settings without subscription
  // Define routes that don't require subscription
  const noSubscriptionRequiredRoutes = [
    "/dashboard/profile",
    "/dashboard/settings",
    "/dashboard/subscriptions",
    "/dashboard/whitelist-requests",
  ];

  const isNoSubscriptionRoute = noSubscriptionRequiredRoutes.some((route) =>
    location.pathname.startsWith(route),
  );

  // Skip subscription check for profile, settings, and subscription management routes
  if (
    requireSubscription &&
    !isNoSubscriptionRoute && // Only check subscription for routes that require it
    user &&
    !user.subscribed &&
    user.role !== "admin" &&
    user.role !== "super_admin" &&
    !user.isWhitelisted &&
    !user.subscriptionState?.subscriptions?.isWhitelisted
  ) {
    console.log("User requires subscription, redirecting to packages");
    console.log("User subscription status:", {
      subscribed: user.subscribed,
      role: user.role,
      isWhitelisted: user.isWhitelisted,
      subscriptionState: user.subscriptionState,
    });
    return <Navigate to="/packages" replace />;
  }

  // DEBUG: Log access granted
  console.log("Access granted to protected route:", {
    path: location.pathname,
    user: {
      id: user?.id,
      email: user?.email,
      role: user?.role,
      subscribed: user?.subscribed,
      isWhitelisted: user?.isWhitelisted,
      subscription: user?.subscription,
      subscriptionState: user?.subscriptionState,
    },
    requirements: {
      adminOnly,
      requireSubscription,
      requireJobPostingAccess,
    },
    isNoSubscriptionRoute,
  });

  return <>{children}</>;
}
